import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import { motion } from 'framer-motion';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';
import { GRADIENTS } from '../theme/tokens';

// --- Pill-track pill button ---

/**
 * PillButton
 * Used inside pill-track variant only.
 * Renders a pill-shaped button with a shared layoutId sliding background for the active state.
 */
function PillButton({ label, isActive, onClick, icon, isDetectedCity, reduced }) {
  const theme = useTheme();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      component="button"
      role="tab"
      aria-selected={isActive}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '999px',
        px: 2,
        py: 0.75,
        cursor: 'pointer',
        background: 'transparent',
        color: isActive ? 'white' : 'text.secondary',
        fontWeight: isActive ? 600 : 400,
        fontSize: '0.875rem',
        lineHeight: 1,
        outline: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        transition: 'color 0.2s',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* Shared sliding gradient background for active pill */}
      {isActive && (
        <motion.div
          layoutId="city-filter-active"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '999px',
            background: GRADIENTS.brand,
            zIndex: 0,
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 26 }}
        />
      )}

      {/* Icon */}
      {icon && (
        <Box
          component="span"
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
          }}
        >
          {icon}
          {/* Pulse ring for detected-city location icon when active */}
          {isDetectedCity && isActive && !reduced && (
            <motion.span
              style={{
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                border: '1.5px solid currentColor',
                pointerEvents: 'none',
              }}
              animate={{ scale: [1, 1.6], opacity: [0.7, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
            />
          )}
        </Box>
      )}

      {/* Label */}
      <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
        {label}
      </Box>
    </Box>
  );
}

PillButton.propTypes = {
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node,
  isDetectedCity: PropTypes.bool,
  reduced: PropTypes.bool.isRequired,
};

PillButton.defaultProps = {
  icon: null,
  isDetectedCity: false,
};

// --- Default variant (original implementation, unchanged visually) ---

function DefaultVariant({
  detectedCity,
  nearbyCities,
  selectedCity,
  onCitySelect,
  isDetecting,
  showAllCitiesOption,
  label,
}) {
  const activeCity = selectedCity || detectedCity;

  if (isDetecting) {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {label && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {label}
          </Typography>
        )}
        <Skeleton variant="rounded" width={80} height={32} />
        <Skeleton variant="rounded" width={70} height={32} />
        <Skeleton variant="rounded" width={90} height={32} />
      </Box>
    );
  }

  if (!detectedCity && nearbyCities.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {label}
        </Typography>
      )}

      {showAllCitiesOption && (
        <Chip
          icon={<PublicIcon />}
          label="All Cities"
          variant={!activeCity ? 'filled' : 'outlined'}
          color={!activeCity ? 'primary' : 'default'}
          onClick={() => onCitySelect(null)}
          sx={{ fontWeight: !activeCity ? 600 : 400 }}
        />
      )}

      {detectedCity && (
        <Chip
          icon={<LocationOnIcon />}
          label={detectedCity}
          variant={activeCity === detectedCity ? 'filled' : 'outlined'}
          color={activeCity === detectedCity ? 'primary' : 'default'}
          onClick={() => onCitySelect(detectedCity)}
          sx={{ fontWeight: activeCity === detectedCity ? 600 : 400 }}
        />
      )}

      {nearbyCities.map((city) => (
        <Chip
          key={city}
          label={city}
          variant={activeCity === city ? 'filled' : 'outlined'}
          color={activeCity === city ? 'primary' : 'default'}
          onClick={() => onCitySelect(city)}
          sx={{ fontWeight: activeCity === city ? 600 : 400 }}
        />
      ))}
    </Box>
  );
}

DefaultVariant.propTypes = {
  detectedCity: PropTypes.string,
  nearbyCities: PropTypes.arrayOf(PropTypes.string),
  selectedCity: PropTypes.string,
  onCitySelect: PropTypes.func.isRequired,
  isDetecting: PropTypes.bool,
  showAllCitiesOption: PropTypes.bool,
  label: PropTypes.string,
};

DefaultVariant.defaultProps = {
  detectedCity: null,
  nearbyCities: [],
  selectedCity: null,
  isDetecting: false,
  showAllCitiesOption: true,
  label: null,
};

// --- Pill-track variant ---

function PillTrackVariant({
  detectedCity,
  nearbyCities,
  selectedCity,
  onCitySelect,
  isDetecting,
  showAllCitiesOption,
  label,
}) {
  const activeCity = selectedCity || detectedCity;
  const reduced = useReducedMotionPreferred();

  if (isDetecting) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {label && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
            {label}
          </Typography>
        )}
        <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: '999px' }} />
        <Skeleton variant="rounded" width={70} height={32} sx={{ borderRadius: '999px' }} />
        <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: '999px' }} />
      </Box>
    );
  }

  if (!detectedCity && nearbyCities.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
          {label}
        </Typography>
      )}

      {showAllCitiesOption && (
        <PillButton
          label="All Cities"
          isActive={!activeCity}
          onClick={() => onCitySelect(null)}
          icon={<PublicIcon sx={{ fontSize: 14 }} />}
          reduced={reduced}
        />
      )}

      {detectedCity && (
        <PillButton
          label={detectedCity}
          isActive={activeCity === detectedCity}
          onClick={() => onCitySelect(detectedCity)}
          icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
          isDetectedCity
          reduced={reduced}
        />
      )}

      {nearbyCities.map((city) => (
        <PillButton
          key={city}
          label={city}
          isActive={activeCity === city}
          onClick={() => onCitySelect(city)}
          reduced={reduced}
        />
      ))}
    </Box>
  );
}

PillTrackVariant.propTypes = {
  detectedCity: PropTypes.string,
  nearbyCities: PropTypes.arrayOf(PropTypes.string),
  selectedCity: PropTypes.string,
  onCitySelect: PropTypes.func.isRequired,
  isDetecting: PropTypes.bool,
  showAllCitiesOption: PropTypes.bool,
  label: PropTypes.string,
};

PillTrackVariant.defaultProps = {
  detectedCity: null,
  nearbyCities: [],
  selectedCity: null,
  isDetecting: false,
  showAllCitiesOption: true,
  label: null,
};

// --- Main export ---

/**
 * CityFilterChips Component
 * Displays city filter chips with user's city highlighted and nearby cities as options.
 *
 * @param {'default'|'pill-track'} [variant='default']
 *   - 'default': original Chip-based UI (visual diff = 0 vs before)
 *   - 'pill-track': animated pill buttons with shared layoutId sliding background
 * @param {string} [accentColor] — reserved for future use in pill-track; not used in default
 */
function CityFilterChips({ variant, accentColor, ...props }) {
  if (variant === 'pill-track') {
    return <PillTrackVariant {...props} />;
  }
  return <DefaultVariant {...props} />;
}

CityFilterChips.propTypes = {
  variant: PropTypes.oneOf(['default', 'pill-track']),
  accentColor: PropTypes.string,
  detectedCity: PropTypes.string,
  nearbyCities: PropTypes.arrayOf(PropTypes.string),
  selectedCity: PropTypes.string,
  onCitySelect: PropTypes.func.isRequired,
  isDetecting: PropTypes.bool,
  showAllCitiesOption: PropTypes.bool,
  label: PropTypes.string,
};

CityFilterChips.defaultProps = {
  variant: 'default',
  accentColor: undefined,
  detectedCity: null,
  nearbyCities: [],
  selectedCity: null,
  isDetecting: false,
  showAllCitiesOption: true,
  label: null,
};

export default CityFilterChips;
